import { Injectable } from "@angular/core";
import { loadStripe } from "@stripe/stripe-js";
import { firestore } from "firebase";
import { stripePublishableKey } from "../interfaces/constants";
import { Library } from "../interfaces/firebase-interfaces";
import { Profile } from "../pages/profile/profile.page";
import { 
  createConnectedAccountDashboardLink, 
  createConnectedAccountOnboardingLink, 
  subscribe, 
  createPortalLink, 
  deleteSubscription,
  createStripeCustomer,
  reactivateSubscription
} from "./cloud-functions";
import { FirebaseService } from "./firebase.service";
import { HelperService } from "./helper.service";

@Injectable({
  providedIn: "root",
})
export class PaymentService {
  constructor(private fireServ: FirebaseService, private helperServ: HelperService) {}

  async initCustomer() {
    const profile = await this.fireServ.getUserProfileData();
    if (profile.stripeId) {
      return profile.stripeId;
    }
    const response = await createStripeCustomer({ user: { email: profile.email, uid: profile.uid } })
      .catch((err) => {
        console.error(`Error from creating stripe customer: ${err}`);
      });

    if (response) {
      return response.data.id;
    }

    return;
  }

  async changeCancelAtPeriodEnd(library: string): Promise<boolean> {
    return this.fireServ
      .getActiveSubscriptionDoc(library, ["id"])
      .then(async (doc) => {
        const response = await reactivateSubscription({ subscriptionId: doc.id });
        return response.data;
      })
      .catch((err) => {
        console.error("Error reactivating: ", err);
        return false;
      });
  }

  // Checkout handler
  async subscribe(library: Library) {
    const sessionId = (
      await subscribe({
        uid: await this.fireServ.getUserProfileData("uid"),
        data: {
          price: library.default_price_id,
          success_url: window.location.origin + "/success",
          cancel_url: window.location.origin + "/store",
          contentId: library.id,
          trial_period_days: library.trial_period_days || null,
          days: library.days || null,
          cycle: library.cycle || "monthly",
          authorId: library.authorId || null,
          created: firestore.Timestamp.now(),
        },
      })
    ).data;
    const stripe = await loadStripe(stripePublishableKey);
    stripe.redirectToCheckout({ sessionId });
  }

  async openStripePortal(customer: Profile) {
    await this.helperServ.presentLoader("You are being redirected. Please wait");

    if (!customer) {
      customer = await this.fireServ.getUserProfileData(`stripeId`);
    }

    const { data } = await createPortalLink({ stripeId: customer.uid, returnUrl: window.location.origin + "/store" });
    window.open(data.url, "_blank");
    await this.helperServ.dismissLoader();
  }

  async unsubscribe(subscriptionId: string): Promise<boolean> {
    const response = await deleteSubscription({ subscriptionId: subscriptionId });

    return response.data;
  }

  async connectWithStripe(customer: Profile) {
    await this.helperServ.presentLoader("You are being redirected. Please wait");

    const { data } = await createConnectedAccountOnboardingLink({
      email: customer.email,
      stripeConnectedAccountId: customer.stripeConnectedAccountId,
    }).catch((err) => {
      console.error(err);
      this.helperServ.presentToast("Error: " + JSON.parse(err.message).raw.message);
      return err;
    });
    window.open(data.url);
    return this.helperServ.dismissLoader();
  }

  async openConnectedAccountDashboard(customer) {
    await this.helperServ.presentLoader("You are being redirected. Please wait");
    console.log("Customer: ", customer);
    return createConnectedAccountDashboardLink({
      stripeConnectedAccountId: customer.stripeConnectedAccountId,
    })
      .then((res) => {
        const { data } = res;
        if (data.url) {
          window.open(data.url);
          return true;
        }
      })
      .catch((err) => {
        console.error(err);
        this.helperServ.presentToast("Error: " + JSON.parse(err.message).raw.message);
        return false;
      })
      .finally(() => this.helperServ.dismissLoader());
  }
}
